/**
 * Die Takt-Editoren (Akkord · Tonart · Taktart · Wiederholungen & Taktstriche · Takt einfügen/
 * löschen · Bass-Spielweise) als EINE Liste.
 *
 * Konsument ist das `NoteActionsOverlay`: die Menüeinträge UND der Kopf der Editor-Ansicht lesen
 * daraus – eine Quelle, damit Label und Icon nicht auseinanderlaufen. Angeboten werden sie in
 * **beiden** Geltungsbereichen des Menüs: im Noten-Bereich als abgesetzter letzter Block, im
 * Takt-Bereich (Klick in die freie Taktfläche) als dessen einziger Inhalt – siehe
 * {@link ./noteActionsMenu}.
 */
import type {EditTab} from './editContext';

export type MeasureEditTab = Exclude<EditTab, 'note'>;

export interface MeasureEditEntry {
    name: MeasureEditTab;
    label: string;
    icon: string;
    /** Textfarbe des Menüeintrags – dieselbe wie der zugehörige Toolbar-Button. */
    colorClass: string;
}

const ENTRIES: readonly MeasureEditEntry[] = [
    {name: 'chord', label: 'Akkord', icon: 'fa6-solid:layer-group', colorClass: 'text-purple-600'},
    {name: 'key', label: 'Tonart', icon: 'fa6-solid:key', colorClass: 'text-amber-600'},
    {name: 'time', label: 'Taktart', icon: 'fa6-solid:clock', colorClass: 'text-sky-600'},
    {name: 'structure', label: 'Wiederholungen & Taktstriche', icon: 'fa6-solid:repeat', colorClass: 'text-teal-600'},
    {name: 'measures', label: 'Takt einfügen/löschen', icon: 'fa6-solid:table-cells-large', colorClass: 'text-rose-600'},
    {name: 'bass', label: 'Bass-Spielweise', icon: 'game-icons:f-clef', colorClass: 'text-violet-600'},
];

/** Die aktuell anbietbaren Editoren, in Darstellungsreihenfolge. „Bass-Spielweise" nur bei
 *  vorhandenem Bass-System – ohne dessen Noten gäbe es nichts zu wählen. */
export function measureEditEntries({hasBassPreview}: { hasBassPreview: boolean }): MeasureEditEntry[] {
    return ENTRIES.filter((entry) => entry.name !== 'bass' || hasBassPreview);
}

/** Eintrag zu einem Reiter (`null` für `'note'` – das ist kein Takt-Editor). */
export function measureEditEntry(tab: EditTab): MeasureEditEntry | null {
    return ENTRIES.find((entry) => entry.name === tab) ?? null;
}
